/**
 * Canonical API client.
 *
 * One module instead of the per-service boilerplate scattered across
 * tenantServices, roleDashboardServices, tenantTicketServices, and ~70 others.
 * Each service used to:
 *   - read REACT_APP_API_URL,
 *   - optionally override it from /config.json,
 *   - implement its own call() helper that wraps authService.apiCall,
 *   - unwrap the { success, data, message } envelope inconsistently.
 *
 * Now: import { api } from './apiClient' and call api.get / api.post / etc.
 *
 * The client owns:
 *   - BASE_URL (env -> /config.json override),
 *   - JSON / FormData body handling,
 *   - envelope unwrap (returns payload.data when present, throws on success:false),
 *   - auth + CSRF via authService.apiCall (unchanged).
 *
 * Use api.raw() when you need status codes, headers, or non-JSON bodies.
 */
import authService from './authService';

type JsonInit = Omit<RequestInit, 'method' | 'body'>;

class ApiClient {
  private baseUrl: string;
  private baseUrlReady: Promise<string>;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || '';
    this.baseUrlReady = this.loadConfig();
  }

  private async loadConfig(): Promise<string> {
    try {
      const response = await fetch('/config.json');
      const config = await response.json();
      if (config?.API_URL) this.baseUrl = config.API_URL;
    } catch {
      // fall back to env
    }
    return this.baseUrl;
  }

  /** Force-refresh BASE_URL from /config.json. Rarely needed. */
  async reloadConfig(): Promise<string> {
    this.baseUrlReady = this.loadConfig();
    return this.baseUrlReady;
  }

  /** Synchronous BASE_URL accessor — may be the env fallback if config.json hasn't resolved yet. */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /** Await this if a call must see the config.json override before firing. */
  async ensureBaseUrl(): Promise<string> {
    return this.baseUrlReady;
  }

  private buildInit(method: string, body: unknown, init: JsonInit = {}): RequestInit {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    const config: RequestInit = { ...init, method };
    if (body !== undefined && body !== null) {
      config.body = isFormData ? (body as FormData) : JSON.stringify(body);
    }
    return config;
  }

  /**
   * Core request. Returns the unwrapped payload.data (envelope) or the
   * payload itself (bare). Throws Error(message) when:
   *   - response.ok is false, or
   *   - payload.success === false.
   */
  async request<T = any>(
    method: string,
    path: string,
    body?: unknown,
    init: JsonInit = {},
  ): Promise<T> {
    await this.baseUrlReady;
    const url = `${this.baseUrl}${path}`;
    const response = await authService.apiCall(url, this.buildInit(method, body, init));

    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      // non-JSON body or empty
    }

    if (!response.ok || (payload && payload.success === false)) {
      const message = payload?.message || `Request failed: ${path}`;
      const err = new Error(message) as Error & { payload?: any; status?: number };
      // Expose the raw body + status so callers can show field-level validation
      // (e.g. { message, issues: [{ field, message }] }) instead of a generic error.
      err.payload = payload;
      err.status = response.status;
      throw err;
    }

    if (payload && typeof payload === 'object' && 'data' in payload) {
      return payload.data as T;
    }
    return payload as T;
  }

  get<T = any>(path: string, init?: JsonInit): Promise<T> {
    return this.request<T>('GET', path, undefined, init);
  }
  post<T = any>(path: string, body?: unknown, init?: JsonInit): Promise<T> {
    return this.request<T>('POST', path, body, init);
  }
  put<T = any>(path: string, body?: unknown, init?: JsonInit): Promise<T> {
    return this.request<T>('PUT', path, body, init);
  }
  patch<T = any>(path: string, body?: unknown, init?: JsonInit): Promise<T> {
    return this.request<T>('PATCH', path, body, init);
  }
  delete<T = any>(path: string, init?: JsonInit): Promise<T> {
    return this.request<T>('DELETE', path, undefined, init);
  }

  /** Multipart upload — accepts a FormData and returns the unwrapped payload. */
  upload<T = any>(
    path: string,
    form: FormData,
    method: 'POST' | 'PUT' = 'POST',
    init?: JsonInit,
  ): Promise<T> {
    return this.request<T>(method, path, form, init);
  }

  /**
   * Escape hatch — returns the raw Response. Use only when you need status
   * codes, headers, or to consume the body as something other than JSON.
   */
  async raw(method: string, path: string, body?: unknown, init: JsonInit = {}): Promise<Response> {
    await this.baseUrlReady;
    const url = `${this.baseUrl}${path}`;
    return authService.apiCall(url, this.buildInit(method, body, init));
  }
}

export const api = new ApiClient();
export default api;
